import React from "react";
import { useBlenderTerrainManifest } from "./useBlenderTerrainManifest";

// BLENDER_PATHWAY_ROUTE_AUTHORITY_V1_8_3C
//
// v1.8.3b proved:
//   - route slot is mounted
//   - pathway_anchors screen projection is correct
//   - endpoint circles render
//   - legacy CSS still suppresses SVG <path> route strokes
//
// v1.8.3c renders the same cubic route as sampled SVG <polyline> geometry.

function finite(value, fallback = null) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function orientationKey(value) {
  const raw = String(value ?? "").trim().toLowerCase();

  if (raw === "affording") return "affords";
  if (raw === "constraining") return "constrains";

  return ["affords", "constrains", "mixed", "unresolved"].includes(raw)
    ? raw
    : "unresolved";
}

function resolutionKey(value) {
  const raw = String(value ?? "").trim().toLowerCase();

  return ["resolved", "thin", "unresolved"].includes(raw)
    ? raw
    : "resolved";
}

const ORIENTATION = Object.freeze({
  affords: "#6d9079",
  constrains: "#b78e78",
  mixed: "#908c72",
  unresolved: "#96948b",
});

const RESOLUTION = Object.freeze({
  resolved: { dash: undefined, opacity: 0.88 },
  thin: { dash: "8 7", opacity: 0.66 },
  unresolved: { dash: "2 8", opacity: 0.48 },
});

function stableBend(pathwayId, index) {
  const text = String(pathwayId ?? index);
  let hash = 2166136261;

  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  const sign = (hash & 1) === 0 ? 1 : -1;
  return sign * (22 + (Math.abs(hash) % 17));
}

function manifestCenter(manifest) {
  const center = manifest?.center?.screen ?? manifest?.center ?? {};

  return {
    x:
      finite(center.canvas_x) ??
      finite(center.x) ??
      500,
    y:
      finite(center.canvas_y) ??
      finite(center.y) ??
      395,
  };
}

function cubicPoint(p0, p1, p2, p3, t) {
  const omt = 1 - t;
  const a = omt * omt * omt;
  const b = 3 * omt * omt * t;
  const c = 3 * omt * t * t;
  const d = t * t * t;

  return {
    x: a * p0.x + b * p1.x + c * p2.x + d * p3.x,
    y: a * p0.y + b * p1.y + c * p2.y + d * p3.y,
  };
}

function sampleCubic(p0, p1, p2, p3, segments = 28) {
  const points = [];

  for (let index = 0; index <= segments; index += 1) {
    const point = cubicPoint(
      p0,
      p1,
      p2,
      p3,
      index / segments,
    );

    points.push(
      `${point.x.toFixed(2)},${point.y.toFixed(2)}`,
    );
  }

  return points.join(" ");
}

function buildCurve(anchor, index, center) {
  const screen = anchor?.screen ?? {};

  const ax =
    finite(screen.canvas_x) ??
    finite(screen.x);

  const ay =
    finite(screen.canvas_y) ??
    finite(screen.y);

  if (ax == null || ay == null) {
    return null;
  }

  const dx = ax - center.x;
  const dy = ay - center.y;
  const distance = Math.hypot(dx, dy);

  if (distance < 8) {
    return null;
  }

  const ux = dx / distance;
  const uy = dy / distance;

  // Fixed presentation constants only.
  // No pathway/root count, evidence, confidence or effect magnitude.
  const startRadius = Math.min(
    126,
    distance * 0.56,
  );

  const endInset = Math.min(
    13,
    distance * 0.035,
  );

  const p0 = {
    x: center.x + ux * startRadius,
    y: center.y + uy * startRadius,
  };

  const p3 = {
    x: ax - ux * endInset,
    y: ay - uy * endInset,
  };

  const nx = -uy;
  const ny = ux;
  const bend = stableBend(
    anchor?.pathway_id,
    index,
  );

  const p1 = {
    x: p0.x + (p3.x - p0.x) * 0.33 + nx * bend,
    y: p0.y + (p3.y - p0.y) * 0.33 + ny * bend,
  };

  const p2 = {
    x: p0.x + (p3.x - p0.x) * 0.72 + nx * bend * 0.42,
    y: p0.y + (p3.y - p0.y) * 0.72 + ny * bend * 0.42,
  };

  return {
    points: sampleCubic(
      p0,
      p1,
      p2,
      p3,
    ),
    x: p3.x,
    y: p3.y,
  };
}

export default function BlenderPathwayRoutes() {
  const manifest = useBlenderTerrainManifest();

  if (!manifest) {
    return null;
  }

  const anchors = Array.isArray(
    manifest.pathway_anchors,
  )
    ? manifest.pathway_anchors
    : [];

  const center = manifestCenter(
    manifest,
  );

  const routes = anchors
    .map((anchor, index) => {
      const geometry = buildCurve(
        anchor,
        index,
        center,
      );

      if (!geometry) {
        return null;
      }

      return {
        id: String(
          anchor?.pathway_id ??
          `pathway:${index}`,
        ),
        orientation: orientationKey(
          anchor?.orientation,
        ),
        resolution: resolutionKey(
          anchor?.resolution,
        ),
        geometry,
      };
    })
    .filter(Boolean);

  if (!routes.length) {
    return null;
  }

  return (
    <>
      {routes.map((route) => {
        const stroke =
          ORIENTATION[route.orientation] ??
          ORIENTATION.unresolved;

        const treatment =
          RESOLUTION[route.resolution] ??
          RESOLUTION.resolved;

        return (
          <g
            key={route.id}
            className="rr-blender-pathway-route"
            data-pathway-id={route.id}
            data-orientation={route.orientation}
            data-resolution={route.resolution}
          >
            <polyline
              points={route.geometry.points}
              className="rr-blender-pathway-route-halo-v183c"
              stroke="#f5f1e7"
              strokeWidth="5.4"
              opacity="0.82"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              style={{
                display: "block",
                visibility: "visible",
              }}
            />

            <polyline
              points={route.geometry.points}
              className="rr-blender-pathway-route-main-v183c"
              stroke={stroke}
              strokeWidth="2.15"
              strokeDasharray={treatment.dash}
              opacity={treatment.opacity}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              style={{
                display: "block",
                visibility: "visible",
              }}
            />

            <circle
              className="rr-blender-pathway-route-anchor"
              cx={route.geometry.x}
              cy={route.geometry.y}
              r="2.8"
              fill={stroke}
              opacity={Math.min(
                0.92,
                treatment.opacity + 0.06,
              )}
            />
          </g>
        );
      })}
    </>
  );
}
